import { useEffect, useState, useRef } from "react";
import CollectionHeroSection from "./CollectionNFTsNewSection";
import Layout from "../../components/layout";
import CollectionNFTsSection from "./CollectionNFTsSection";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import {
  getCollectionDetails,
  getCollectionNFTs,
  searchCollectionNFTs,
} from "../../slices/collectionDetails/collectionDetailsThunks";
import { AIRankingNFTs, CollectionDetailsRecord, Loading } from "./types";
import { ThumbNailImg } from "../../shared/icons";
import { BarChartWrapper } from "./style";
import RadarChartComponent from "../CollectionDetails/RadarGraph";

const CollectionDetails = () => {
  const { id } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const [aiRankingNFTs, setAIRankingNFTs] = useState<AIRankingNFTs[] | []>([]);
  const [collectionDetails, setCollectionDetails] =
    useState<CollectionDetailsRecord | null>(null);
  const [isLoading, setIsLoading] = useState<Loading>({
    collectionDetails: false,
    aiRankingNFTs: false,
  });
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const nftListingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getCollectionDetailsRecord();
    getAIRankingNFTsList();
  }, []);

  const scrollToNFTListing = () => {
    if (nftListingRef.current) {
      const targetPosition = nftListingRef.current.offsetTop - 100;
      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  };

  const getCollectionDetailsRecord = () => {
    setIsLoading((prev: any) => ({ ...prev, collectionDetails: true }));
    dispatch(getCollectionDetails(id))
      .then((res: any) => {
        console.log("res", res?.payload?.data?.data);
        if (res?.error) {
          throw new Error(res?.payload);
        }
        const collection = res?.payload?.data?.data;
        const mappedCollectionDetails = {
          featureImage: collection?.featureImage || ThumbNailImg,
          heading: collection?.name,
          creator: collection?.creator,
          owner: collection?.owner,
          description: collection?.description,
          chain:
            collection?.chain === "hedera"
              ? "HBAR"
              : collection?.chain === "ethereum"
              ? "ETH"
              : "POL",
          score: collection?.score || 0,
          externalLink: collection?.siteLink,
        };

        setCollectionDetails(mappedCollectionDetails);
      })
      .catch((err) => {
        console.log({err})
      })
      .finally(() => {
        setIsLoading((prev: any) => ({ ...prev, collectionDetails: false }));
      });
  };

  const getAIRankingNFTsList = (
    name: string = "",
    rank_by: string = "",
    limit: number = 10,
    page: number = 1,
    isLoadingMore: boolean = false
  ) => {
    setIsLoading((prev: any) => ({ ...prev, aiRankingNFTs: !isLoadingMore }));
    setIsLoadingMore(isLoadingMore);
    dispatch(
      getCollectionNFTs({ collectionId: id, limit, page, name, rank_by })
    )
      .then((res: any) => {
        console.log("res", res?.payload?.data?.data);
        if (res?.error) {
          throw new Error(res?.payload);
        }
        setTotalPages(res?.payload?.data?.meta?.totalPages);
        setCurrentPage(res?.payload?.data?.meta?.currentPage);
        const mappedAIRankingNFTs = res?.payload?.data?.data?.map(
          (item: any) => ({
            id: item?._id,
            nftChain: item?.chain,
            nftName: item?.name,
            nftImage: item?.fileUrl,
            price: item?.price || 0,
            ecoScore: item?.score || 0,
            networkId: item?.networkId || 1,
            donation: "0.0",
          })
        );

        const currentPage = res?.payload?.data?.meta?.currentPage;
        if (page === 1 && !isLoadingMore) {
          // Initial load or applying filters (reset data)
          setAIRankingNFTs(mappedAIRankingNFTs);
        } else if (isLoadingMore && currentPage > 1) {
          // Loading more NFTs (append data)
          setAIRankingNFTs((prevRecords: any) => [
            ...prevRecords,
            ...mappedAIRankingNFTs,
          ]);
        } else if (!name && !rank_by) {
          // No filters applied (initial unfiltered load or reset)
          setAIRankingNFTs((prevRecords: any) => [
            ...prevRecords,
            ...mappedAIRankingNFTs,
          ]);
        } else {
          // Filters applied (reset data)
          setAIRankingNFTs(mappedAIRankingNFTs);
        }
      })
      .catch((err) => {
        console.log({err})
      })
      .finally(() => {
        setIsLoading((prev: any) => ({ ...prev, aiRankingNFTs: false }));
        setIsLoadingMore(false);
      });
  };

  const handleSearch = (
    query: string,
    limit: number = 10,
    page: number = 1,
    isLoadingMore: boolean = false
  ) => {
    if (!isLoadingMore) {
      setAIRankingNFTs([]);
    }
    if (query?.length > 0) {
      setSearch(query);
      setIsLoading((prev: any) => ({ ...prev, aiRankingNFTs: !isLoadingMore }));
      setIsLoadingMore(isLoadingMore);
      dispatch(
        searchCollectionNFTs({ collectionId: id, keyword: query, limit, page })
      )
        .then((res: any) => {
          console.log("res search", res?.payload?.data?.data);
          if (res?.error) {
            throw new Error(res?.payload);
          }
          setTotalPages(res?.payload?.data?.meta?.totalPages);
          setCurrentPage(res?.payload?.data?.meta?.currentPage);
          const mappedAIRankingNFTs = res?.payload?.data?.data?.map(
            (item: any) => ({
              id: item?._id,
              nftChain: item?.chain,
              nftName: item?.name,
              nftImage: item?.fileUrl,
              price: item?.price || 0,
              ecoScore: item?.score || 0,
              networkId: item?.networkId || 1,
              donation: "0.0",
            })
          );
          if (res?.payload?.data?.data?.length > 0) {
            setAIRankingNFTs((prevRecords: any) => [
              ...prevRecords,
              ...mappedAIRankingNFTs,
            ]);
          } else {
            setAIRankingNFTs([]);
          }
        })
        .catch((err) => {
         console.log({err})
        })
        .finally(() => {
          setIsLoading((prev: any) => ({ ...prev, aiRankingNFTs: false }));
          setIsLoadingMore(false);
        });
    } else {
      setSearch(query);
      setAIRankingNFTs([]);
      getAIRankingNFTsList();
    }
  };

  return (
    <Layout fullScreen>
      <CollectionHeroSection
        isLoading={isLoading?.collectionDetails}
        collectionDetails={collectionDetails}
      />
      <BarChartWrapper>
        <RadarChartComponent
          id={id}
          name={collectionDetails?.heading}
          score={collectionDetails?.score}
          networkId={collectionDetails?.chain}
        />
      </BarChartWrapper>
      <CollectionNFTsSection
        aiRankingNFTs={aiRankingNFTs}
        isLoading={isLoading?.aiRankingNFTs}
        currentPage={currentPage}
        totalPages={totalPages}
        getMoreAIRankingNFTs={getAIRankingNFTsList}
        isLoadingMore={isLoadingMore}
        search={search}
        handleSearch={handleSearch}
      />
    </Layout>
  );
};

export default CollectionDetails;
